import React, { useState, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { format, isToday, isTomorrow } from 'date-fns';
import {
  ArrowLeft,
  Plus,
  Star,
  BookOpen,
  Briefcase,
  Sparkles,
  Target,
  Calendar,
  Clock,
  User,
  Edit,
  Trash2,
  ExternalLink,
  Filter,
  CheckCircle,
  Clock4,
  Lightbulb,
  ClipboardList,
} from 'lucide-react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { useRecsIdeas } from '../../hooks/useRecsIdeas';
import UnifiedBig3Planner from './UnifiedBig3Planner';
import SmartInputSection from '../Messages/SmartInputSection';
import AddActivityForm from '../Libraries/AddActivityForm';
import AddIdeaForm from '../Ideas/AddIdeaForm';
import ActivityLibrary from '../Libraries/ActivityLibrary';


const EnhancedPlanningHub = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'ideas';
  
  const { currentUser } = useFamilyContext();
  const { ideas, deleteIdea } = useRecsIdeas();
  
  const [filter, setFilter] = useState('all');
  const [showAddIdea, setShowAddIdea] = useState(false);
  const [showAddActivity, setShowAddActivity] = useState(false);

  // Category configurations
  const categoryConfig = {
    book: {
      name: 'Book',
      icon: BookOpen,
      color: 'bg-indigo-500',
      bgColor: 'bg-indigo-100',
      textColor: 'text-indigo-700'
    },
    project: {
      name: 'Project',
      icon: Briefcase,
      color: 'bg-pink-500',
      bgColor: 'bg-pink-100',
      textColor: 'text-pink-700'
    },
    experience: {
      name: 'Experience',
      icon: Sparkles,
      color: 'bg-coral-500',
      bgColor: 'bg-coral-100',
      textColor: 'text-coral-600'
    },
    learning: {
      name: 'Learning',
      icon: Target,
      color: 'bg-amber-500',
      bgColor: 'bg-amber-100',
      textColor: 'text-amber-700'
    }
  };



  // Process ideas data
  const processedIdeas = useMemo(() => {
    let filtered = ideas || [];
    
    if (filter !== 'all') {
      filtered = filtered.filter(idea => idea.category === filter);
    }
    
    return filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [ideas, filter]);

  // Calculate stats
  const stats = useMemo(() => {
    const total = ideas?.length || 0;
    const inProgress = ideas?.filter(idea => {
      return Object.values(idea.kidResponses || {}).some(response => 
        response.status === 'interested'
      );
    }).length || 0;
    const completed = ideas?.filter(idea => {
      return Object.values(idea.kidResponses || {}).every(response => 
        response.status !== 'not-responded'
      );
    }).length || 0;

    return { total, inProgress, completed };
  }, [ideas]);

  // Get category counts
  const categoryCounts = useMemo(() => {
    const counts = { all: ideas?.length || 0 };
    Object.keys(categoryConfig).forEach(category => {
      counts[category] = ideas?.filter(idea => idea.category === category).length || 0;
    });
    return counts;
  }, [ideas]);

  // Handle tab change
  const handleTabChange = (tab) => {
    setSearchParams({ tab });
  };





  // Handle idea deletion
  const handleIdeaDelete = async (ideaId) => {
    if (window.confirm('Are you sure you want to delete this idea?')) {
      await deleteIdea(ideaId);
    }
  };

  // Get idea status
  const getIdeaStatus = (idea) => {
    const responses = Object.values(idea.kidResponses || {});
    if (responses.length === 0) return 'new';
    if (responses.some(r => r.status === 'interested')) return 'in-progress';
    if (responses.every(r => r.status !== 'not-responded')) return 'completed';
    return 'new';
  };

  // Format relative time
  const getRelativeTime = (dateString) => {
    const date = new Date(dateString);
    if (isToday(date)) return 'Today';
    if (isTomorrow(date)) return 'Tomorrow';
    return format(date, 'MMM d');
  };

  return (
    <div className="enhanced-planning-hub min-h-screen bg-stone-50">
      {/* Header */}
      <div className="bg-white border-b border-stone-200 sticky top-0 z-50">
        <div className="px-4 py-3">
          {/* Top row: Back button, Section name with color bar, Add button */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 text-stone-600 hover:bg-stone-100 rounded-lg transition-colors"
                style={{ minWidth: '44px', minHeight: '44px' }}
                aria-label="Go back"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              
              <div className="flex items-center gap-3">
                {/* Color bar */}
                <div className="w-2 h-6 bg-indigo-500 rounded-full"></div>
                <div>
                  <h1 className="text-xl font-bold text-slate-900 tracking-tight">Ideas & Planning</h1>
                </div>
              </div>
            </div>

            {/* Add Button */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  if (activeTab === 'ideas') {
                    setShowAddIdea(true);
                  } else if (activeTab === 'activities') {
                    setShowAddActivity(true);
                  }
                }}
                className="bg-slate-900 text-white p-2 rounded-lg hover:bg-slate-800 transition-colors flex items-center gap-1"
                style={{ minWidth: '44px', minHeight: '44px' }}
              >
                <Plus className="w-4 h-4" />
                {activeTab === 'ideas' ? <Lightbulb className="w-4 h-4" /> : <ClipboardList className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
        
        {/* Bottom row: Tab Navigation */}
        <div className="px-0">
          <div className="flex">
            <button 
              onClick={() => handleTabChange('ideas')}
              className={`flex-1 px-4 py-4 text-sm font-medium transition-all duration-150 border-b-2 ${
                activeTab === 'ideas'
                  ? 'border-indigo-500 text-slate-900 bg-indigo-50'
                  : 'border-transparent text-stone-600 hover:text-slate-900 hover:bg-stone-50'
              }`}
              style={{ minHeight: '48px' }}
            >
              <div className="flex items-center justify-center gap-2">
                <Lightbulb className="w-4 h-4" />
                <span>Ideas & Opportunities</span>
              </div>
            </button>
            <button 
              onClick={() => handleTabChange('activities')}
              className={`flex-1 px-4 py-4 text-sm font-medium transition-all duration-150 border-b-2 ${
                activeTab === 'activities'
                  ? 'border-indigo-500 text-slate-900 bg-indigo-50'
                  : 'border-transparent text-stone-600 hover:text-slate-900 hover:bg-stone-50'
              }`}
              style={{ minHeight: '48px' }}
            >
              <div className="flex items-center justify-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>Daily Activities</span>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-4 space-y-4 pb-20">
        {activeTab === 'ideas' ? (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-white rounded-xl p-4 border border-stone-200">
                <div className="text-2xl font-bold text-slate-900">{stats.total}</div>
                <div className="text-sm text-stone-600">Total Ideas</div>
              </div>
              <div className="bg-white rounded-xl p-4 border border-stone-200">
                <div className="text-2xl font-bold text-indigo-600">{stats.inProgress}</div>
                <div className="text-sm text-stone-600">In Progress</div>
              </div>
              <div className="bg-white rounded-xl p-4 border border-stone-200">
                <div className="text-2xl font-bold text-emerald-600">{stats.completed}</div>
                <div className="text-sm text-stone-600">Completed</div>
              </div>
            </div>

            {/* Filter Bar */}
            <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
              <button
                onClick={() => setFilter('all')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 whitespace-nowrap ${
                  filter === 'all'
                    ? 'bg-slate-900 text-white'
                    : 'bg-white border border-stone-300 text-slate-900 hover:bg-stone-100'
                }`}
              >
                All Ideas
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                  filter === 'all'
                    ? 'bg-white/20 text-white'
                    : 'bg-stone-200 text-stone-700'
                }`}>
                  {categoryCounts.all}
                </span>
              </button>
              
              {Object.entries(categoryConfig).map(([key, config]) => {
                const Icon = config.icon;
                return (
                  <button
                    key={key}
                    onClick={() => setFilter(key)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 whitespace-nowrap ${
                      filter === key
                        ? 'bg-slate-900 text-white'
                        : 'bg-white border border-stone-300 text-slate-900 hover:bg-stone-100'
                    }`}
                  >
                    <Icon className="w-3 h-3" />
                    {config.name}
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                      filter === key
                        ? 'bg-white/20 text-white'
                        : 'bg-stone-200 text-stone-700'
                    }`}>
                      {categoryCounts[key]}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Ideas Grid */}
            <div className="space-y-4">
              {processedIdeas.length === 0 ? (
                <div className="text-center py-16">
                  <Lightbulb className="w-16 h-16 text-stone-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-stone-600 mb-2">No ideas yet</h3>
                  <p className="text-stone-500 mb-6">Start by adding your first idea or opportunity!</p>
                  <button
                    onClick={() => setShowAddIdea(true)}
                    className="px-6 py-3 bg-indigo-500 text-white rounded-xl font-semibold hover:bg-indigo-600 transition-colors"
                  >
                    <Plus className="w-4 h-4 inline mr-2" />
                    Add Your First Idea
                  </button>
                </div>
              ) : (
                processedIdeas.map((idea) => {
                  const config = categoryConfig[idea.category] || categoryConfig.experience;
                  const Icon = config.icon;
                  const status = getIdeaStatus(idea);
                  
                  return (
                    <div key={idea.id} className="bg-white rounded-xl border border-stone-200 p-4 hover:shadow-md transition-all">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${config.bgColor}`}>
                            <Icon className={`w-4 h-4 ${config.textColor}`} />
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-900">{idea.title}</h3>
                            <p className="text-sm text-stone-600">{config.name}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            status === 'new' ? 'bg-azure-100 text-azure-700' :
                            status === 'in-progress' ? 'bg-amber-100 text-amber-700' :
                            'bg-emerald-100 text-emerald-700'
                          }`}>
                            {status === 'new' ? 'New' : status === 'in-progress' ? 'In Progress' : 'Completed'}
                          </span>
                          <button
                            onClick={() => handleIdeaDelete(idea.id)}
                            className="p-1 text-stone-400 hover:text-stone-600 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      
                      {idea.description && (
                        <p className="text-stone-600 text-sm mb-3">{idea.description}</p>
                      )}
                      
                      <div className="flex items-center justify-between text-xs text-stone-500">
                        <span>Added by {idea.addedBy}</span>
                        <span>{getRelativeTime(idea.created_at)}</span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </>
        ) : (
          /* Daily Activities Tab */
          <div className="space-y-6">
            {/* Activity Library */}
            <ActivityLibrary />
          </div>
        )}
      </div>

      {/* Add Idea Modal */}
      {showAddIdea && (
        <AddIdeaForm onClose={() => setShowAddIdea(false)} />
      )}

      {/* Add Activity Modal */}
      {showAddActivity && (
        <AddActivityForm 
          selectedKid={currentUser?.id}
          kidName={currentUser?.name}
          onClose={() => setShowAddActivity(false)} 
        />
      )}
    </div>
  );
};

export default EnhancedPlanningHub;